import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Card, DatePicker, Flex, Input, Select, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';

import { ReactComponent as SearchIcon } from '../../../assets/magnifier.svg';
import Button from '../../../custom/button/button';
import { useDebounce } from '../../../hooks/useDebounce';
import { useDownload } from '../../../hooks/useDownload';
import { usePagination } from '../../../hooks/usePagination';
import api from '../../../utils/api';
import { buildQuery } from '../../../utils/buildQuery';
import { formatFullDate } from '../../../utils/formatDate';
import styles from '../../setup/styles.module.scss';

const { RangePicker } = DatePicker;

interface PaymentRecord {
  applicantNumber: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  customerEmail: string;
  customerPhone: string;
  paymentChannel: string;
  paymentDate: string;
  amount: number;
  paymentReference: string;
  paymentStatus: string;
  paymentType: string;
  totalAmount: number | null;
  discountAmount: number | null;
  couponCode: string | null;
}

interface PaymentFilters {
  ApplicantNumber: string;
  FirstName: string;
  LastName: string;
  PaymentChannel: string;
  StartDate: string;
  EndDate: string;
  PaymentReference: string;
  PaymentType: number | '';
}

const PAYMENT_TYPE_OPTIONS = [
  { label: 'Application Fee', value: 1 },
  { label: 'Acceptance Fee', value: 2 },
];

const STATUS_COLOR: Record<string, string> = {
  successful: 'success',
  failed: 'error',
  pending: 'warning',
};

const getPaymentHistory = async (params: Record<string, any>) => {
  const url = buildQuery(params);
  return (await api.get(`/Payment/PaymentHistoryFilter${url}`))?.data;
};

const downloadPaymentHistory = async (params: Record<string, any>) => {
  const url = buildQuery(params);
  return (await api.get(`/Payment/DownloadPaymentHistoryFilter${url}`, { responseType: 'blob' }))
    ?.data as Blob;
};

const EMPTY_FILTERS: PaymentFilters = {
  ApplicantNumber: '',
  FirstName: '',
  LastName: '',
  PaymentChannel: '',
  StartDate: '',
  EndDate: '',
  PaymentReference: '',
  PaymentType: '',
};

const PaymentReport = () => {
  const { notification } = App.useApp();
  const { currentPage, onChange } = usePagination();
  const { downloadExcelFile } = useDownload();

  const [filters, setFilters] = useState<PaymentFilters>(EMPTY_FILTERS);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const debouncedReference = useDebounce(filters.PaymentReference, 500);

  const buildParams = (overrides: Record<string, any> = {}) => ({
    PageNumber: currentPage,
    PageSize: 10,
    ApplicantNumber: filters.ApplicantNumber || undefined,
    FirstName: filters.FirstName || undefined,
    LastName: filters.LastName || undefined,
    PaymentChannel: filters.PaymentChannel || undefined,
    StartDate: filters.StartDate || undefined,
    EndDate: filters.EndDate || undefined,
    PaymentReference: debouncedReference || undefined,
    PaymentType: filters.PaymentType || undefined,
    ...overrides,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['payment-history', buildParams()],
    queryFn: () => getPaymentHistory(buildParams()),
  });

  const downloadMutation = useMutation({ mutationFn: downloadPaymentHistory });

  const records: PaymentRecord[] = data?.data?.data ?? [];
  const totalRecords: number = data?.data?.totalRecords ?? 0;

  const setFilter = (key: keyof PaymentFilters, value: any) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleDateChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setDateRange(dates);
    if (dates?.[0] && dates?.[1]) {
      setFilter('StartDate', dates[0].toISOString());
      setFilter('EndDate', dates[1].toISOString());
    } else {
      setFilter('StartDate', '');
      setFilter('EndDate', '');
    }
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setDateRange(null);
  };

  const parseBlobError = async (error: any): Promise<string> => {
    const fallback = 'Unable to download the payment report. Please try again.';
    try {
      const blob: Blob = error?.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        const json = JSON.parse(text);
        return json?.message || fallback;
      }
    } catch {}
    return error?.response?.data?.message || fallback;
  };

  const handleDownload = async (downloadAll: boolean) => {
    const activeFilters = {
      ApplicantNumber: filters.ApplicantNumber || undefined,
      FirstName: filters.FirstName || undefined,
      LastName: filters.LastName || undefined,
      PaymentChannel: filters.PaymentChannel || undefined,
      StartDate: filters.StartDate || undefined,
      EndDate: filters.EndDate || undefined,
      PaymentReference: debouncedReference || undefined,
      PaymentType: filters.PaymentType || undefined,
    };

    const params = {
      PageNumber: 1,
      PageSize: 100000,
      ...(downloadAll ? {} : activeFilters),
    };

    await downloadMutation.mutateAsync(params, {
      onSuccess: (blob) => {
        downloadExcelFile(
          blob,
          downloadAll ? 'Payment_Report_All.xlsx' : 'Payment_Report_Filtered.xlsx',
        );
      },
      onError: async (error: any) => {
        const description = await parseBlobError(error);
        notification.error({ message: 'Download Failed', description });
      },
    });
  };

  const columns: ColumnsType<PaymentRecord> = [
    {
      key: 'sn',
      title: 'S/N',
      width: 55,
      render: (_, __, index) => (currentPage - 1) * 10 + index + 1,
    },
    {
      key: 'applicantNumber',
      title: 'App. No.',
      dataIndex: 'applicantNumber',
    },
    {
      key: 'name',
      title: 'Full Name',
      render: (_, r) =>
        `${r.firstName}${r.middleName ? ` ${r.middleName}` : ''} ${r.lastName}`,
    },
    {
      key: 'customerEmail',
      title: 'Email',
      dataIndex: 'customerEmail',
    },
    {
      key: 'customerPhone',
      title: 'Phone',
      dataIndex: 'customerPhone',
    },
    {
      key: 'paymentChannel',
      title: 'Channel',
      dataIndex: 'paymentChannel',
      render: (val: string) =>
        val ? val.charAt(0).toUpperCase() + val.slice(1).toLowerCase() : '-',
    },
    {
      key: 'paymentType',
      title: 'Type',
      dataIndex: 'paymentType',
    },
    {
      key: 'amount',
      title: 'Amount (₦)',
      dataIndex: 'amount',
      align: 'right',
      render: (val: number) => (val != null ? `₦${val.toLocaleString()}` : '-'),
    },
    {
      key: 'paymentReference',
      title: 'Reference',
      dataIndex: 'paymentReference',
    },
    {
      key: 'paymentStatus',
      title: 'Status',
      dataIndex: 'paymentStatus',
      render: (status: string) => (
        <Tag color={STATUS_COLOR[status?.toLowerCase()] ?? 'default'}>{status}</Tag>
      ),
    },
    {
      key: 'paymentDate',
      title: 'Date',
      dataIndex: 'paymentDate',
      render: (val: string) => formatFullDate(val),
    },
    {
      key: 'couponCode',
      title: 'Coupon',
      dataIndex: 'couponCode',
      render: (val: string | null) => val ?? '-',
    },
  ];

  return (
    <div>
      <section className="space-between">
        <h3>Payment Report</h3>
        <Flex gap="8px" wrap="wrap">
          <Button
            text="Download Filtered"
            onClick={() => handleDownload(false)}
            isLoading={downloadMutation.isPending}
            disabled={downloadMutation.isPending}
          />
          <Button
            text="Download All"
            onClick={() => handleDownload(true)}
            isLoading={downloadMutation.isPending}
            disabled={downloadMutation.isPending}
            variant="text"
          />
        </Flex>
      </section>

      <section className={styles.card}>
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontWeight: 600, marginBottom: 8, color: '#8F0024' }}>Filters</p>
          <Flex gap="8px" wrap="wrap" align="center">
            <RangePicker
              value={dateRange}
              onChange={handleDateChange}
              placeholder={['Start Date', 'End Date']}
              format="DD/MM/YYYY"
              style={{ minWidth: 220, borderColor:'#8F0024' }}
            />
            <Input
              placeholder="Applicant Number"
              value={filters.ApplicantNumber}
              onChange={(e) => setFilter('ApplicantNumber', e.target.value)}
              style={{ width: 160, borderColor:'#8F0024' }}
              allowClear
            />
            <Input
              placeholder="First Name"
              value={filters.FirstName}
              onChange={(e) => setFilter('FirstName', e.target.value)}
              style={{ width: 140, borderColor:'#8F0024' }}
              allowClear
            />
            <Input
              placeholder="Last Name"
              value={filters.LastName}
              onChange={(e) => setFilter('LastName', e.target.value)}
              style={{ width: 140,borderColor:'#8F0024' }}
              allowClear
            />
            {/* <Input
              placeholder="Payment Channel"
              value={filters.PaymentChannel}
              onChange={(e) => setFilter('PaymentChannel', e.target.value)}
              style={{ width: 160, borderColor:'#8F0024' }}
              allowClear
            /> */}
            <Select
              placeholder="Payment Type"
              value={filters.PaymentType || undefined}
              allowClear
              options={PAYMENT_TYPE_OPTIONS}
              style={{ width: 160, borderColor:'#8F0024' }}
              onChange={(value) => setFilter('PaymentType', value ?? '')}
            />
            <Input
              placeholder="Search by Reference..."
              prefix={<SearchIcon style={{ width: 14, height: 14 }} />}
              value={filters.PaymentReference}
              onChange={(e) => setFilter('PaymentReference', e.target.value)}
              style={{ width: 220 ,borderColor:'#8F0024'}}
              allowClear
            />
            <Button
              text="Clear Filters"
              variant="text"
              onClick={handleClearFilters}
              type="button"
            />
          </Flex>
        </div>

        <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>
            Showing <strong>{records.length}</strong> of <strong>{totalRecords}</strong> records
          </p>
        </Flex>

        <Table
          dataSource={records.map((r, i) => ({ ...r, key: `${r.paymentReference}-${i}` }))}
          columns={columns}
          loading={isLoading}
          scroll={{ x: true }}
          pagination={{
            position: ['bottomCenter'],
            current: currentPage,
            total: totalRecords,
            onChange,
            pageSize: 10,
            showSizeChanger: false,
          }}
        />
      </section>
    </div>
  );
};

export default PaymentReport;
